import React, { useEffect, useState, useRef, useMemo, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { createUseStyles } from 'react-jss';
import pluralize from 'pluralize';
import {
  Theme as theme,
  AlertService,
  Search,
  FilterIcon,
  Button,
  Select,
  SelectBox,
  CheckboxWithLabel,
  OverlayService,
  Flyout,
  Timing,
  SelectOption,
  usePolling,
  MiniTooltip,
  Theme,
  DaterangeFilter,
  SparkleIcon,
  InformationIcon,
  LoadingLine,
} from '@spoiler-alert/ui-library';
import moment from 'moment';
import { AcceptShiftedOfferListings, AwardSuggestedOffersByInventory } from '../../graphql/mutations';
import RecommendedNegotiationsManual from '../../components/banners/recommended-negotiations-manual';
import {
  trucklanePollingQuery,
  trucklaneViewQuery,
  AwardSummaryQuery,
  ActiveOfferShiftsQuery,
  OfferListingsByInventoryQuery,
  InventoryFilterParamsQuery,
} from '../../graphql/queries';
import { buildSegmentedListingsQuery } from './build-segmented-listings-query';
import { TitleService } from '../../services';
import BuyersFlyup from './buyers-flyup';
import { OfferComparisonStrings } from '../../string-resources';
import OfferListings from './offer-listings';
import {
  featureFlags,
  logisticsTermEnum,
  negotiationStatusEnum,
  offerStatus,
  truckTypeEnum,
  trucklaneStatusEnum,
  subscriptionTier,
} from '../../enums';
import OfferComparisonTable from './offer-comparison-table';
import { Breadcrumbs, Store, UserFilters } from '../../store';
import RecalculationBanner from '../trucklane/recalculation-banner';
import StaleTabBanner from '../trucklane/stale-tab-banner';
import trucklaneChannelStatus from '../../enums/trucklane-channel-status';
import client from '../../apollo/client';
import deleteCachedFieldsOnOfferReviewPollingQuery from '../../apollo/cache-helpers/delete-cached-fields-on-offer-review-polling-query';
import checkFeature from '../../helpers/check-feature-flag';
import SuggestedNegotiationsVisibilityBanner from '../../components/banners/suggested-negotiations-visibility';
import { TrucklaneActionInProgressContext } from '../trucklane/trucklane-utility';
import OfferShiftContext, { filterShiftsByInventoryIds, formatShiftsForAcceptance } from './offer-shift-context';

const useStyles = createUseStyles({
  offerPage: {},
  offerGridHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  offerGridLeftSide: {
    display: 'flex',
    flexDirection: 'row',
  },
  offerGrid: {
    border: '1px #D1D6DA solid',
    borderRadius: 2,
    display: 'flex',
    margin: '25px 0 0',
    flexDirection: 'row',
    minHeight: 'calc(100vh - 98px)',
  },
  offerGridTable: {
    position: 'relative',
    flex: '0 1 100%',
    borderRight: `1px solid ${theme.borderColor}`,
  },
  offerGridDataTable: {},
  offerGridSearchInput: {
    width: 386,
    marginRight: 8,
    marginLeft: '24px',
  },
  filterButton: {
    marginRight: 24,
  },
  pageActions: {
    marginLeft: 'auto',
    display: 'flex',
  },
  filtersHeaderContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: 24,
  },
  offerGridFiltersHeader: {
    margin: 0,
    marginRight: 'auto',
    fontSize: '1rem',
    fontWeight: 500,
  },
  offerGridClearFilters: {
    color: theme.linkTextColor,
    cursor: 'pointer',
    fontSize: '0.875rem',
  },
  tiedCheckbox: {
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    marginTop: 10,
  },
  tiedCheckboxText: {
    marginLeft: 10,
  },
  filterSection: {
    marginBottom: '48px',
  },
  filterSectionTitle: {
    fontSize: '16px',
    fontWeight: 'normal',
    letterSpacing: '-0.18px',
    margin: 0,
  },
  filterSectionDescription: {
    fontSize: '12px',
    borderBottom: `1px solid ${Theme.borderColor}`,
    paddingBottom: 1,
  },
  filterHeader: {
    margin: '24px 0',
    fontWeight: 'normal',
    '& h2': {
      fontSize: '14px',
      fontWeight: 500,
      marginBottom: 0,
    },
  },
  flyoutContainer: {
    height: '100%',
    paddingRight: 0,
  },
  flyout: {
    overflowY: 'auto',
    padding: '24px',
  },
  flyoutContents: {
    padding: 0,
  },
  selectBox: {
    marginRight: '8px',
  },
  alert__title: {
    height: 24,
    fontSize: 16,
    fontWeight: 'bold',
    letterSpacing: 'normal',
    textAlign: 'left',
    marginBottom: '8px',
  },
  buttonIcon: {
    '& svg': {
      maxWidth: 20,
      maxHeight: 20,
      width: 20,
      height: 20,
      marginTop: 2,
    },
  },
  offerShiftContextNotice: {
    padding: 12,
    display: 'flex',
    gap: 8,
    alignSelf: 'stretch',
    alignItems: 'center',
    borderRadius: 4,
    background: theme.teal10,
    marginTop: 24,
    '& a': {
      marginLeft: 'auto',
    },
  },
  offerShiftContextIcon: {
    width: 16,
    height: 16,
    fill: theme.teal,
  },
});

const initialPageSize = 200;
const pageSize = 700;

const defaultFilterState = {
  sites: [],
  status: [
    { text: 'Offers Made', value: 'Offers Made' },
    { text: 'Offers Awarded', value: 'Offers Awarded' },
    { text: 'Partially Awarded', value: 'Partially Awarded' },
  ],
  showTied: false,
  belowReservePrice: false,
  suggestedAward: false,
  postAwardUpdates: false,
  categories: [],
  businessUnits: [],
  truckTypes: [],
  negotiationStatus: [],
  logistics: [],
  inventoryTypes: [],
  codeDate: { start: undefined, end: undefined },
  trucklaneStatus: [],
  distributionLists: [],
  importBatches: [],
  buyers: [],
  offerShifts: [],
};

const OfferComparisonProgressive = ({ user, history }) => {
  const classes = useStyles();
  const reservePriceExceptionsEnabled = checkFeature(featureFlags.reservePriceExceptions);
  const segmentedActiveListingsQuery = useMemo(() => buildSegmentedListingsQuery(user.site), [user.site]);

  useState(() => {
    if (!Store.shouldRefreshOfferComparison) return;
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'listedInventoriesQuery' });
    client.cache.gc();
    Store.refreshOfferComp(false);
  });

  const {
    data,
    loading: initialPageLoad,
    error,
    fetchMore,
  } = useQuery(segmentedActiveListingsQuery, {
    variables: {
      limit: initialPageSize,
    },
  });

  const { data: invFiltersData, loading: loadingInvFilters } = useQuery(InventoryFilterParamsQuery);
  const [awardSuggestedOffersByInventory, { loading: bulkAwardSuggestionsLoading }] = useMutation(AwardSuggestedOffersByInventory);
  const [acceptOfferShifts, { loading: acceptingOfferShiftLoading }] = useMutation(AcceptShiftedOfferListings);

  const acceptingSuggestions = bulkAwardSuggestionsLoading || acceptingOfferShiftLoading;

  const [searchText, setSearchText] = useState('');
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [filters, setFilters] = useState({ ...defaultFilterState, ...UserFilters.filters.offerComparison });
  const [activeFilters, setActiveFilters] = useState(filters);
  const [showFilters, setShowFilters] = useState(false);
  const [noDataMessageIndex, setNoDataMessageIndex] = useState(0);
  const [recalculationRequired, setRecalculationRequired] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [staleTab, setStaleTab] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(5000);
  const [checkedInventoryIds, setCheckedInventoryIds] = useState([]);
  const [shouldFetchMore, setShouldFetchMore] = useState(true);
  const [loading, setLoading] = useState(initialPageLoad);
  const filteredInventoryIds = useRef([]);
  const saasLiteCustomer = user.site.subscriptionTier === subscriptionTier.saasLite;
  const { actionInProgress } = useContext(TrucklaneActionInProgressContext);
  const offerShiftContextValue = useContext(OfferShiftContext);

  useEffect(() => {
    if (!data) return;
    if (data.listedInventoriesQuery.length > 0 && shouldFetchMore) {
      const last = data.listedInventoriesQuery[data.listedInventoriesQuery.length - 1];
      fetchMore({
        variables: {
          fetchMoreAfter: last._id,
          limit: pageSize,
        },
      })
        .then(({ data }) => {
          if (data.listedInventoriesQuery.length < pageSize) {
            setShouldFetchMore(false);
            setLoading(false);
          }
        })
        .catch((error) => {
          AlertService.alert({
            type: 'caution',
            message: <span>{error.message}</span>,
            autoDismiss: false,
          });
          setShouldFetchMore(false);
          setLoading(false);
        });
    } else if (data.listedInventoriesQuery.length === 0) {
      setShouldFetchMore(false);
      setLoading(false);
    }
  }, [data, fetchMore, shouldFetchMore]);

  const pollResponse = usePolling({
    idleInterval: pollingInterval,
    poll: true,
    query: trucklanePollingQuery,
    responseKey: 'trucklanePollingQuery',
    parameters: { pollingDetails: { origin: selectedInventory?.siteId, truckType: selectedInventory?.truckType } },
    skip: !selectedInventory || recalculationRequired || staleTab,
  });

  const pollingRef = useRef();

  const messageInterval = useRef();

  const suggestedNegotiationsEnabled = checkFeature(featureFlags.suggestedNegotiations);

  const legacySuggestedNegotiations = checkFeature(featureFlags.legacySuggestedNegotiations);

  const inventoryFilterParameters = invFiltersData?.currentUserQuery?.inventoryFilterParameters || null;

  const receivedInventories = data?.listedInventoriesQuery || null;

  const inventories = useMemo(() => {
    return data?.listedInventoriesQuery || [];
  }, [data?.listedInventoriesQuery]);

  const listingCount = inventories.length;

  const clearFilters = () =>
    setFilters({
      sites: [],
      status: [],
      categories: [],
      businessUnits: [],
      truckTypes: [],
      negotiationStatus: [],
      logistics: [],
      inventoryTypes: [],
      codeDate: { start: undefined, end: undefined },
      distributionLists: [],
      trucklaneStatus: [],
      importBatches: [],
      buyers: [],
      offerShifts: [],
    });

  const resetPolling = (clearRecalculationRequired = true) => {
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'trucklanePollingQuery' });
    setRecalculating(false);
    if (clearRecalculationRequired) {
      setRecalculationRequired(false);
    }
    setPollingInterval(5000);
  };

  const handleRowClick = (row) => {
    setSelectedInventory(row);
    resetPolling(false);
  };

  const handleSelectInventoryById = (inventoryId) => {
    const inventory = inventories.find((inv) => inv._id === inventoryId);
    if (inventory) setSelectedInventory(inventory);
  };

  useEffect(() => {
    const pendingId = history.location?.state?.selectedInventoryId;
    if (!pendingId || !inventories.length) return;
    const inventory = inventories.find((inv) => inv._id === pendingId);
    if (inventory) setSelectedInventory(inventory);
    history.replace({ ...history.location, state: null });
  }, [inventories, history]);

  useEffect(() => {
    // Once the tab is stale, polling is stopped (see `skip`) and nothing below applies until reload.
    if (staleTab) return;
    // The server flags a poll as stale when this tab's lane isn't in the user's current site/cycle
    // (another tab switched impersonation, or the transaction cycle rolled over). Ask for a reload
    // instead of raising the recalc banner or waiting on a lane that will never come back.
    if (pollResponse?.stale) {
      setStaleTab(true);
      setRecalculating(false);
      setRecalculationRequired(false);
      return;
    }
    if (recalculating) {
      const trucklaneValid = pollResponse?.trucklaneChannels[0]?.status === trucklaneChannelStatus.VALID;
      const bidReallocationComplete = !pollResponse?.bidReallocation?.processing;

      if (trucklaneValid && bidReallocationComplete) {
        client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'activeOfferShiftsQuery' });
        client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'offerListingsByInventoryQuery' });
        client.cache.gc();
        resetPolling();
      }
    }
    if (!recalculating) {
      const trucklaneInvalid = pollResponse?.trucklaneChannels[0]?.status === trucklaneChannelStatus.INVALID;
      const trucklaneChanged =
        pollingRef.current?.trucklaneChannels[0]?._id === pollResponse?.trucklaneChannels[0]?._id &&
        pollingRef.current?.trucklaneChannels[0]?.updatedAt !== pollResponse?.trucklaneChannels[0]?.updatedAt;
      const bidReallocationProcessing = !!pollResponse?.bidReallocation?.processing;
      const bidReallocationChanged =
        pollResponse?.bidReallocation &&
        pollingRef.current?.bidReallocation &&
        !pollResponse?.bidReallocation?.processing &&
        pollingRef.current.bidReallocation.updatedAt !== pollResponse.bidReallocation.updatedAt;

      if (trucklaneInvalid || trucklaneChanged || bidReallocationChanged || bidReallocationProcessing) {
        const pollingLane = pollingRef.current?.trucklaneChannels[0];
        const cacheExists = client.readQuery({ query: trucklaneViewQuery });
        setRecalculationRequired(true);
        if (pollingLane && cacheExists) {
          selectedInventory.offerListings.forEach((offer) => {
            client.cache.modify({
              id: client.cache.identify({
                id: `${pollingLane.truckType}:${offer.logisticsTerm}:${offer.sellerLocationId}:${offer.buyerDestinationId}:${offer.transactionCycleId}`,
                __typename: 'Trucklane',
              }),
              fields: {
                status() {
                  return 'Invalid';
                },
              },
            });
          });
          Store.invalidateTrucklaneChannel(pollingLane);
        }
      } else if (
        pollingRef.current?.trucklaneChannels[0]?._id !== pollResponse?.trucklaneChannels[0]?._id &&
        pollResponse?.trucklaneChannels[0]?.status === trucklaneChannelStatus.VALID
      ) {
        setRecalculationRequired(false);
      }
    }
    pollingRef.current = pollResponse;
  }, [pollResponse, recalculating, selectedInventory, staleTab]);

  const handleSearch = Timing.debounce((text) => setSearchText(text), 300);

  const handleFilterChange = useCallback(
    (prop, value) => {
      setFilters({
        ...filters,
        [prop]: value,
      });
      setCheckedInventoryIds([]);
    },
    [filters],
  );

  const handleCodeDateRangeChange = useCallback(
    (start, end) => {
      handleFilterChange('codeDate', { start: moment(start), end: moment(end) });
    },
    [handleFilterChange],
  );

  const removeItem = (array, item) => {
    const index = array.indexOf(item);
    if (index > -1) {
      array.splice(index, 1);
    }
    return array;
  };

  const handleCheckboxFilterChange = useCallback(
    (prop, event) =>
      handleFilterChange(prop, event.checked ? [...filters[prop], event.value] : removeItem(filters[prop], event.value), [handleFilterChange]),
    [filters, handleFilterChange],
  );

  const handleCheckFilterChange = useCallback((event) => handleFilterChange(event.value, event.checked), [handleFilterChange]);

  const toggleFilters = () => {
    if (showFilters) {
      OverlayService.hide();
      setActiveFilters(filters);
      UserFilters.updateFilters('offerComparison', filters);
    } else {
      OverlayService.show();
    }
    setShowFilters(!showFilters);
  };

  const noDataMessageIndexIncrement = useCallback(() => {
    if (noDataMessageIndex >= OfferComparisonStrings.loadingMessages.length - 1 || listingCount > 0) window.clearInterval(messageInterval.current);
    setNoDataMessageIndex(noDataMessageIndex + 1);
  }, [listingCount, noDataMessageIndex]);

  useEffect(() => {
    messageInterval.current = window.setInterval(noDataMessageIndexIncrement, 2000);
    return () => {
      clearInterval(messageInterval.current);
    };
  }, [noDataMessageIndexIncrement]);

  const noDataMessage = () => {
    if (error) return error.message;
    return loading ? OfferComparisonStrings.loadingMessages[noDataMessageIndex] : OfferComparisonStrings.noData;
  };

  useEffect(() => {
    TitleService.setTitles('Offer Comparison');
    Breadcrumbs.set([
      {
        url: '',
        title: `Offer Comparison ${listingCount > 0 ? `(${listingCount} items${loading ? '...' : ''})` : ''}`,
      },
    ]);
  }, [listingCount, loading]);

  const offerStatusFilterOptions = useMemo(() => {
    return (
      <Select key="status" multiple label="Status" onChange={handleFilterChange.bind(this, 'status')} selectedItems={filters.status}>
        <SelectOption key={offerStatus.noOffers} checkbox value={offerStatus.noOffers}>
          {offerStatus.noOffers}
        </SelectOption>
        <SelectOption key={offerStatus.offersAvailable} checkbox value={offerStatus.offersAvailable}>
          {offerStatus.offersAvailable}
        </SelectOption>
        <SelectOption key={offerStatus.partiallyAwarded} checkbox value={offerStatus.partiallyAwarded}>
          {offerStatus.partiallyAwarded}
        </SelectOption>
        <SelectOption key={offerStatus.offersAwarded} checkbox value={offerStatus.offersAwarded}>
          {offerStatus.offersAwarded}
        </SelectOption>
      </Select>
    );
  }, [handleFilterChange, filters]);

  const categoryFilterOptions = useMemo(() => {
    const selectData =
      loadingInvFilters || !inventoryFilterParameters?.categories
        ? []
        : inventoryFilterParameters.categories.map((category) => (
            <SelectOption checkbox key={category.id} value={category.id}>
              {category.name}
            </SelectOption>
          ));
    return (
      <Select key="categories" multiple label="Category" onChange={handleFilterChange.bind(this, 'categories')} selectedItems={filters.categories}>
        {selectData}
      </Select>
    );
  }, [inventoryFilterParameters, handleFilterChange, filters, loadingInvFilters]);

  const businessUnitFilterOptions = useMemo(() => {
    const selectData =
      loadingInvFilters || !inventoryFilterParameters?.businessUnits
        ? []
        : inventoryFilterParameters.businessUnits.map((businessUnit) => (
            <SelectOption checkbox key={businessUnit.id} value={businessUnit.id}>
              {businessUnit.name}
            </SelectOption>
          ));
    return (
      <Select
        key="businessUnits"
        multiple
        label="Business Units"
        onChange={handleFilterChange.bind(this, 'businessUnits')}
        selectedItems={filters.businessUnits}
      >
        {selectData}
      </Select>
    );
  }, [inventoryFilterParameters, filters, handleFilterChange, loadingInvFilters]);

  const inventoryTypeFilterOptions = useMemo(() => {
    const selectData =
      loadingInvFilters || !inventoryFilterParameters?.inventoryTypes
        ? []
        : inventoryFilterParameters.inventoryTypes.map((inventoryType) => (
            <SelectOption checkbox key={inventoryType.id} value={inventoryType.id}>
              {inventoryType.name}
            </SelectOption>
          ));
    return (
      <Select
        key="inventoryTypes"
        multiple
        label="Inventory Type"
        onChange={handleFilterChange.bind(this, 'inventoryTypes')}
        selectedItems={filters.inventoryTypes}
      >
        {selectData}
      </Select>
    );
  }, [inventoryFilterParameters, loadingInvFilters, handleFilterChange, filters]);

  const distributionListFilterOptions = useMemo(() => {
    const selectData =
      loadingInvFilters || !inventoryFilterParameters?.distributionLists
        ? []
        : inventoryFilterParameters.distributionLists.map((distributionList) => (
            <SelectOption checkbox key={distributionList.id} value={distributionList.id}>
              {distributionList.name}
            </SelectOption>
          ));
    return (
      <Select
        key="distributionLists"
        multiple
        label="Distribution Lists"
        onChange={handleFilterChange.bind(this, 'distributionLists')}
        selectedItems={filters.distributionLists}
      >
        {selectData}
      </Select>
    );
  }, [inventoryFilterParameters, loadingInvFilters, filters, handleFilterChange]);

  const truckTypeFilterOptions = useMemo(() => {
    const truckTypes = [truckTypeEnum.dry, truckTypeEnum.refrigerated, truckTypeEnum.frozen];
    return (
      <div key="truckType" className={classes.filterHeader}>
        <h2>Truck Type</h2>
        {truckTypes.map((truckType) => (
          <CheckboxWithLabel
            id={`truckType-${truckType}`}
            label={truckType}
            key={`truckType-${truckType}`}
            value={truckType}
            checked={filters.truckTypes.includes(truckType)}
            onChecked={handleCheckboxFilterChange.bind(this, 'truckTypes')}
          />
        ))}
      </div>
    );
  }, [handleCheckboxFilterChange, filters, classes]);

  const offerShiftOfferCompFilterEnabled = checkFeature(featureFlags.offerShiftOfferCompFilter);

  const offerShiftFilterOptions = useMemo(() => {
    if (!offerShiftOfferCompFilterEnabled) return null;
    return (
      <div key="offer-shift" className={classes.filterHeader}>
        <h2>Offer Shifts</h2>
        <CheckboxWithLabel
          id="offerShifts-show"
          label="Show Only Shifts"
          key="offerShifts-show"
          value={'show'}
          checked={filters.offerShifts.includes('show')}
          onChecked={handleCheckboxFilterChange.bind(this, 'offerShifts')}
        />
      </div>
    );
  }, [handleCheckboxFilterChange, filters, classes, offerShiftOfferCompFilterEnabled]);

  const negotiationStatusFilterOptions = useMemo(() => {
    const negotiationStatusTypes = [negotiationStatusEnum.staged, negotiationStatusEnum.published, negotiationStatusEnum.received];
    return (
      <div key="negotiationStatus" className={classes.filterHeader}>
        <h2>Negotiation Status</h2>
        {negotiationStatusTypes.map((negotiationStatusType) => (
          <CheckboxWithLabel
            id={`negotiationStatus-${negotiationStatusType}`}
            label={negotiationStatusType}
            key={`negotiationStatus-${negotiationStatusType}`}
            value={negotiationStatusType}
            checked={filters.negotiationStatus.includes(negotiationStatusType)}
            onChecked={handleCheckboxFilterChange.bind(this, 'negotiationStatus')}
          />
        ))}
      </div>
    );
  }, [classes, handleCheckboxFilterChange, filters]);

  const logisticsFilterOptions = useMemo(() => {
    const logistics = [logisticsTermEnum.pickup, logisticsTermEnum.delivery];
    return (
      <div key="logistics" className={classes.filterHeader}>
        <h2>Logistics</h2>
        {logistics.map((logisticsTerm) => (
          <CheckboxWithLabel
            id={`logistics-${logisticsTerm}`}
            label={logisticsTerm}
            key={`logistics-${logisticsTerm}`}
            value={logisticsTerm}
            checked={filters.logistics.includes(logisticsTerm)}
            onChecked={handleCheckboxFilterChange.bind(this, 'logistics')}
          />
        ))}
      </div>
    );
  }, [classes, handleCheckboxFilterChange, filters]);

  const locationFilterOptions = useMemo(() => {
    const sortedSites = [...user.siteHierarchy].sort((a, b) => {
      const nameA = a.siteName.toUpperCase();
      const nameB = b.siteName.toUpperCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });
    return (
      <Select key="sites" label="Location" multiple search onChange={handleFilterChange.bind(this, 'sites')} selectedItems={filters.sites}>
        {sortedSites.map((site) => (
          <SelectOption checkbox key={site._id} value={site._id}>
            {site.siteName}
          </SelectOption>
        ))}
      </Select>
    );
  }, [user, handleFilterChange, filters]);

  const codeDateFilter = useMemo(() => {
    const { start, end } = filters.codeDate || { start: undefined, end: undefined };
    return (
      <DaterangeFilter
        key={'filter-codedate'}
        onChange={handleCodeDateRangeChange}
        position="topright"
        initialValues={{ start, end }}
        label={'Code Date Range'}
      />
    );
  }, [handleCodeDateRangeChange, filters]);

  const trucklaneStatusFilterOptions = useMemo(() => {
    return (
      <div key="trucklaneStatus" className={classes.filterHeader}>
        <h2>Truck Lane Status</h2>
        <CheckboxWithLabel
          id="trucklaneStatus-active"
          label={trucklaneStatusEnum.active}
          value={trucklaneStatusEnum.active}
          checked={filters.trucklaneStatus.includes(trucklaneStatusEnum.active)}
          onChecked={handleCheckboxFilterChange.bind(this, 'trucklaneStatus')}
        />
        <CheckboxWithLabel
          id="trucklaneStatus-inactive"
          label={trucklaneStatusEnum.inactive}
          value={trucklaneStatusEnum.inactive}
          checked={filters.trucklaneStatus.includes(trucklaneStatusEnum.inactive)}
          onChecked={handleCheckboxFilterChange.bind(this, 'trucklaneStatus')}
        />
      </div>
    );
  }, [handleCheckboxFilterChange, filters, classes]);

  const importBatchFilterOptions = useMemo(() => {
    const selectData =
      loadingInvFilters || !inventoryFilterParameters?.importBatches
        ? []
        : inventoryFilterParameters.importBatches.map((batch) => (
            <SelectOption checkbox key={batch.id} value={batch.id}>
              {batch.name}
            </SelectOption>
          ));
    return (
      <Select
        key="importBatches"
        label="Upload History"
        multiple
        search
        onChange={handleFilterChange.bind(this, 'importBatches')}
        selectedItems={filters.importBatches}
      >
        {selectData}
      </Select>
    );
  }, [inventoryFilterParameters, loadingInvFilters, handleFilterChange, filters]);

  const buyerSitesFilterOptions = useMemo(() => {
    const buyerSet = new Set();
    receivedInventories?.forEach((inv) => inv.buyerSiteNames.forEach((buyerName) => buyerSet.add(buyerName)));
    const selectData = Array.from(buyerSet).map((buyerName, index) => {
      return (
        <SelectOption checkbox key={index} value={buyerName}>
          {buyerName}
        </SelectOption>
      );
    });
    return (
      <Select key="buyers" multiple label="Buyer Sites" onChange={handleFilterChange.bind(this, 'buyers')} selectedItems={filters.buyers}>
        {selectData}
      </Select>
    );
  }, [receivedInventories, handleFilterChange, filters.buyers]);

  const offerShiftContextNotice = useMemo(() => {
    if (offerShiftContextValue?.currentShifts?.length > 0) {
      return (
        <div className={classes.offerShiftContextNotice}>
          <InformationIcon className={classes.offerShiftContextIcon} />
          <div>Some inventory lines are hidden from view</div>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              offerShiftContextValue.setCurrentShifts([]);
            }}
          >
            Back to Previous View
          </a>
        </div>
      );
    }
    return null;
  }, [offerShiftContextValue, classes]);

  const renderedInventoryFilters = useMemo(() => {
    return [
      locationFilterOptions,
      codeDateFilter,
      categoryFilterOptions,
      offerStatusFilterOptions,
      businessUnitFilterOptions,
      inventoryTypeFilterOptions,
      distributionListFilterOptions,
      importBatchFilterOptions,
      truckTypeFilterOptions,
      offerShiftFilterOptions,
    ];
  }, [
    locationFilterOptions,
    codeDateFilter,
    categoryFilterOptions,
    offerStatusFilterOptions,
    businessUnitFilterOptions,
    inventoryTypeFilterOptions,
    distributionListFilterOptions,
    truckTypeFilterOptions,
    importBatchFilterOptions,
    offerShiftFilterOptions,
  ]);

  const renderedOffersFilters = useMemo(() => {
    return [
      buyerSitesFilterOptions,
      <div className={classes.filterHeader} key="isTied">
        <div>
          <CheckboxWithLabel id="showTied" label="Tied" value="showTied" checked={filters.showTied} onChecked={handleCheckFilterChange} />
        </div>
        <div key="belowReservePrice">
          <CheckboxWithLabel
            id="belowReservePrice"
            label="Below Reserve Price"
            value="belowReservePrice"
            checked={filters.belowReservePrice}
            onChecked={handleCheckFilterChange}
          />
        </div>
        <div key="suggestedAward">
          <CheckboxWithLabel
            id="suggestedAward"
            label="Suggested Award"
            value="suggestedAward"
            checked={filters.suggestedAward}
            onChecked={handleCheckFilterChange}
          />
        </div>
        <div key="postAwardUpdates">
          <CheckboxWithLabel
            id="postAwardUpdates"
            label="Updated Since Awarded"
            value="postAwardUpdates"
            checked={filters.postAwardUpdates}
            onChecked={handleCheckFilterChange}
          />
        </div>
        {offerShiftContextValue?.currentShifts?.length > 0 && (
          <div key="reallocation-context">
            <CheckboxWithLabel
              id="ReallocationContext"
              label="Bid Shift Context"
              value="ReallocationContext"
              checked={true}
              onChecked={() => offerShiftContextValue.setCurrentShifts([])}
            />
          </div>
        )}
      </div>,
      negotiationStatusFilterOptions,
      logisticsFilterOptions,
      trucklaneStatusFilterOptions,
    ];
  }, [
    buyerSitesFilterOptions,
    classes.filterHeader,
    filters.showTied,
    filters.belowReservePrice,
    filters.suggestedAward,
    filters.postAwardUpdates,
    handleCheckFilterChange,
    offerShiftContextValue,
    negotiationStatusFilterOptions,
    logisticsFilterOptions,
    trucklaneStatusFilterOptions,
  ]);

  const filterCount = () => {
    return Object.keys(activeFilters).filter((fkey) => {
      const filter = activeFilters[fkey];
      if (Array.isArray(filter)) {
        return filter.length > 0;
      }
      if (filter === true) return true;
      if (filter.start !== undefined || filter.end !== undefined) return true;
      return false;
    }).length;
  };

  const displaySmartAwardSettings = () => {
    history.push('/organization/settings/smart-awarding');
    return;
  };

  const selectedInventoryId = selectedInventory ? selectedInventory._id : '';
  const selectedInventoryDescription = selectedInventory ? selectedInventory.description : '';
  const filtersAppliedCount = filterCount();

  const refreshTrucklaneData = () => {
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'trucklanePollingQuery' });
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'trucklaneViewQuery' });
    client.cache.gc();
    Store.clearTrucklaneChannels();
    setRecalculationRequired(false);
    setPollingInterval(1000);
    setRecalculating(true);
  };

  const allSelected = () => {
    return (
      filteredInventoryIds.current.length > 0 && checkedInventoryIds.length > 0 && checkedInventoryIds.length === filteredInventoryIds.current.length
    );
  };

  const partialSelected = () => {
    return (
      filteredInventoryIds.current.length > 0 && checkedInventoryIds.length > 0 && checkedInventoryIds.length < filteredInventoryIds.current.length
    );
  };

  const selectAll = (result) => {
    setCheckedInventoryIds(result.checked ? filteredInventoryIds.current : []);
  };

  const handleCheck = (e) => {
    if (!e || e.value === undefined) return;
    if (checkedInventoryIds.includes(e.value)) {
      setCheckedInventoryIds((prev) => prev.filter((checkedId) => checkedId !== e.value));
    } else {
      setCheckedInventoryIds((prev) => [...prev, e.value]);
    }
  };

  const canAwardSuggested = useMemo(() => {
    if (checkedInventoryIds.length === 0 || inventories.length === 0) return false;
    const firstSuggested = inventories.find(
      (inv) => checkedInventoryIds.includes(inv._id) && inv.offerListings?.some((ol) => ol.status === 'ACTIVE' && ol.suggestions?.award?.suggested),
    );
    if (firstSuggested) return true;
    return false;
  }, [checkedInventoryIds, inventories]);

  const canAwardShifts = useMemo(() => {
    if (!offerShiftContextValue || !offerShiftContextValue.shiftMap || offerShiftContextValue.shiftMap.size === 0) return false;
    const reallocatedBids = filterShiftsByInventoryIds(checkedInventoryIds, offerShiftContextValue.shiftMap);
    if (reallocatedBids.length > 0) return true;
    return false;
  }, [checkedInventoryIds, offerShiftContextValue]);

  const bulkAwardSuggestions = () => {
    (async () => {
      try {
        if (canAwardShifts) {
          const reallocatedBids = filterShiftsByInventoryIds(checkedInventoryIds, offerShiftContextValue.shiftMap);
          const refetchQueries = [
            {
              query: AwardSummaryQuery,
            },
            {
              query: ActiveOfferShiftsQuery,
            },
            {
              query: OfferListingsByInventoryQuery,
              variables: { inventoryIds: checkedInventoryIds },
            },
          ];
          const acceptShiftResult = await acceptOfferShifts({
            variables: {
              offerShifts: formatShiftsForAcceptance(reallocatedBids),
            },
            refetchQueries,
          });
          if (acceptShiftResult.data?.acceptShiftedOfferListings.errors && acceptShiftResult.data.acceptShiftedOfferListings.errors.length > 0) {
            throw new Error(acceptShiftResult.data.acceptShiftedOfferListings.errors[0].message);
          }
        }
        if (canAwardSuggested) {
          const awardSuggestedResult = await awardSuggestedOffersByInventory({
            variables: {
              inventoryIds: checkedInventoryIds,
              trucklaneGroupChannelId: checkedInventoryIds.includes(selectedInventoryId) ? pollingRef.current?.trucklaneChannels[0]?._id : null,
              trucklaneGroupChannelUpdatedAt: checkedInventoryIds.includes(selectedInventoryId)
                ? pollingRef.current?.trucklaneChannels[0]?.updatedAt
                : null,
            },
            update: (cache) => {
              deleteCachedFieldsOnOfferReviewPollingQuery(cache);
            },
            refetchQueries: [
              {
                query: AwardSummaryQuery,
              },
            ],
          });
          if (!awardSuggestedResult.data.awardSuggestedOffersByInventory.errors) {
            const message = `${awardSuggestedResult.data.awardSuggestedOffersByInventory.offerListings.length} suggested ${pluralize(
              'offer',
              awardSuggestedResult.data.awardSuggestedOffersByInventory.offerListings.length,
            )} ${pluralize('has', awardSuggestedResult.data.awardSuggestedOffersByInventory.offerListings.length)} been successfully awarded`;
            AlertService.alert({ type: 'success', message, autoDismiss: true, dismissDelay: 5000 });
          } else if (
            awardSuggestedResult.data.awardSuggestedOffersByInventory.errors.filter((e) => e.code === 'AWARD_OFFER_NO_OFFER_LISTINGS_ERROR').length
          ) {
            AlertService.alert({
              type: 'caution',
              message: (
                <div>
                  <div className={classes.alert__title}>No Suggestions Found</div>
                  <span>We did not find any suggested awards on the selected inventory. Try filtering for “Suggested Award”</span>
                </div>
              ),
              autoDismiss: true,
              dismissDelay: 5000,
            });
          } else {
            throw Error(awardSuggestedResult.data.awardSuggestedOffersByInventory.errors[0].message);
          }
        }
      } catch {
        AlertService.alert({
          type: 'warning',
          autoDismiss: true,
          message: (
            <span>
              We were unable to award this offer due to an unknown error. Our team has been notified and are looking into the issue. Please contact
              customer support if the issue persists.
            </span>
          ),
          dismissDelay: 5000,
        });
      } finally {
        setCheckedInventoryIds([]);
      }
    })();
  };

  return (
    <div className={classes.offerPage}>
      {!saasLiteCustomer && suggestedNegotiationsEnabled && <SuggestedNegotiationsVisibilityBanner history={history} />}
      {(saasLiteCustomer || legacySuggestedNegotiations) && <RecommendedNegotiationsManual history={history} />}
      <div className={classes.offerGridHeader}>
        <div className={classes.offerGridLeftSide}>
          <SelectBox id="select-all" className={classes.selectBox} partial={partialSelected()} all={allSelected()} onSelect={selectAll} />
          {recalculationRequired ? (
            <MiniTooltip text={<div>Recalculation Required</div>}>
              <Button
                onClick={bulkAwardSuggestions}
                iq
                className={classes.buttonIcon}
                icon={SparkleIcon}
                disabled={!checkedInventoryIds.length || recalculationRequired || recalculating || !(canAwardShifts || canAwardSuggested)}
                loading={acceptingSuggestions}
                loadingText="Awarding Suggestions..."
              >
                Award Suggestions
              </Button>
            </MiniTooltip>
          ) : (
            <Button
              className={classes.buttonIcon}
              onClick={bulkAwardSuggestions}
              iq
              icon={SparkleIcon}
              disabled={!checkedInventoryIds.length || recalculationRequired || recalculating || !(canAwardShifts || canAwardSuggested)}
              loading={acceptingSuggestions}
              loadingText="Awarding Suggestions..."
            >
              Award Suggestions
            </Button>
          )}
          <Search onChange={handleSearch} className={classes.offerGridSearchInput} />
          <Button secondary className={classes.filterButton} icon={FilterIcon} onClick={toggleFilters} filter={filtersAppliedCount !== 0}>
            {filtersAppliedCount > 0 ? `${filtersAppliedCount} ${pluralize('Filter', filtersAppliedCount)}` : 'Filter'}
          </Button>
        </div>
        {!saasLiteCustomer && !reservePriceExceptionsEnabled && (
          <Button secondary onClick={displaySmartAwardSettings}>
            Smart Award Settings
          </Button>
        )}
      </div>
      {offerShiftContextNotice}
      <div className={classes.offerGrid}>
        <div className={classes.offerGridTable}>
          <LoadingLine loading={loading} />
          <div data-testid="TableWrapper" className={classes.offerGridDataTable}>
            <OfferComparisonTable
              user={user}
              loading={loading}
              inventories={inventories}
              selectedInventory={selectedInventory}
              handleRowClick={handleRowClick}
              searchText={searchText}
              filters={activeFilters}
              noDataMessage={noDataMessage()}
              filteredInventoryIds={filteredInventoryIds}
              checkedInventoryIds={checkedInventoryIds || []}
              handleCheck={handleCheck}
            />
          </div>
        </div>
        <OfferListings
          inventoryId={selectedInventoryId}
          description={selectedInventoryDescription}
          availableQuantity={inventories.filter((inv) => inv._id === selectedInventoryId)[0]?.availableQuantity}
          user={user}
          stickyOffset={45}
          showSuggestions={true}
          trucklaneChannel={pollResponse?.trucklaneChannels[0]}
          recalculationRequired={recalculationRequired}
          recalculating={recalculating}
          isBulkAwarding={acceptingSuggestions && checkedInventoryIds.includes(selectedInventoryId)}
          onSelectInventory={handleSelectInventoryById}
        />
      </div>
      {recalculationRequired && !actionInProgress && !staleTab && <RecalculationBanner recalculateAction={refreshTrucklaneData} />}
      {staleTab && <StaleTabBanner />}
      <BuyersFlyup selectedInventoryId={selectedInventoryId} user={user} history={history} />
      <Flyout
        position="right"
        open={showFilters}
        onHide={toggleFilters}
        classes={{ flyout__container: classes.flyoutContainer, flyout__contents: classes.flyoutContents }}
      >
        <div className={classes.flyout}>
          <div className={classes.filtersHeaderContainer} data-element="filters-header">
            <h4 className={classes.offerGridFiltersHeader}>{OfferComparisonStrings.filters}</h4>
            <a className={classes.offerGridClearFilters} tabIndex="1" onClick={clearFilters} data-element="clear-filters">
              {OfferComparisonStrings.clearFilters}
            </a>
          </div>
          <div className={classes.filterSection}>
            <h5 className={classes.filterSectionTitle}>Inventory</h5>
            <div className={classes.filterSectionDescription}>Show inventory that matches...</div>
            {renderedInventoryFilters}
          </div>
          <div className={classes.filterSection}>
            <h5 className={classes.filterSectionTitle}>Offers</h5>
            <div className={classes.filterSectionDescription}>Show inventory with offers that are...</div>
            {renderedOffersFilters}
          </div>
        </div>
      </Flyout>
      {showFilters ? <div id="hideScroll" style={{ display: 'none' }} /> : <div id="showScroll" style={{ display: 'none' }} />}
    </div>
  );
};

OfferComparisonProgressive.propTypes = {
  user: PropTypes.object,
  history: PropTypes.object,
};

export default OfferComparisonProgressive;
